'use client';

import { useState, useEffect, useRef } from 'react';
import { Session } from 'next-auth';
import { useSession, SessionProvider } from 'next-auth/react';
import { useCompletion, experimental_useObject as useObject } from 'ai/react';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Search, Sparkles } from 'lucide-react';
import { Slider } from '@/components/ui/slider';

interface PersonaIdea {
    prompt: string;
    reason: string;
    relevance: number;
    examples: string[];
    rolePrompt: string;
    company_count: number;
    bid: number;
    distribution?: {
        min: number;
        max: number;
        mean: number;
        std: number;
    };
    pricePerBooking?: number;
}

enum AnalysisStep {
    IDLE = 'idle',
    ANALYZING = 'analyzing',
    GENERATING = 'generating',
    PRICING = 'pricing',
    READY = 'ready',
    SENDING = 'sending'
}

function NosAgentsHeroWithSession({ sessionRef }: { sessionRef: React.MutableRefObject<Session | null> }) {
    const [inputValue, setInputValue] = useState('');
    const [step, setStep] = useState<AnalysisStep>(AnalysisStep.IDLE);
    const [analyzedBusiness, setAnalyzedBusiness] = useState('');
    const [personas, setPersonas] = useState<PersonaIdea[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);

    // Website analysis using completion API
    const { complete: analyzeWebsite, completion: websiteAnalysis, isLoading: isAnalyzing } = useCompletion({
        api: '/api/crawl-sites/stream',
        streamProtocol: 'text',
        onFinish: (prompt: any, completion: any) => {
            // Auto-trigger persona generation immediately when crawl completes
            console.log('Website analysis completed:', completion);
            if (completion) {
                setAnalyzedBusiness(completion);
                setStep(AnalysisStep.GENERATING);

                // Trigger persona generation immediately
                generatePersonas({
                    prompts: [],
                    query: completion
                });
            }
        }
    });

    // Persona generation using object API
    const { object: streamedPersonas, submit: generatePersonas, isLoading: isGeneratingPersonas } = useObject({
        api: '/api/persona-bids/stream',
        schema: z.object({
            results: z.array(
                z.object({
                    prompt: z.string(),
                    reason: z.string(),
                    relevance: z.number(),
                    examples: z.array(z.string()),
                    rolePrompt: z.string(),
                    company_count: z.number(),
                    bid: z.number(),
                    distribution: z.object({
                        min: z.number(),
                        max: z.number(),
                        mean: z.number(),
                        std: z.number()
                    })
                })
            )
        }),
        onFinish: (event: any) => {
            console.log('Persona generation completed:', event);
            // Step transition is now handled in useEffect
        },
        onError: (error: any) => {
            console.error('Persona generation error:', error);
            setStep(AnalysisStep.IDLE);
        }
    });

    // Update analyzed business when website analysis completes (for display purposes)
    useEffect(() => {
        if (websiteAnalysis && websiteAnalysis !== analyzedBusiness) {
            // Only update if we haven't already set it in onFinish
            if (!analyzedBusiness) {
                setAnalyzedBusiness(websiteAnalysis);
            }
        }
    }, [websiteAnalysis, analyzedBusiness]);

    // Update personas when streaming (real-time updates)
    useEffect(() => {
        if (streamedPersonas?.results) {
            const newPersonas = streamedPersonas.results.map((result: any, index: number) => ({
                prompt: result.prompt,
                reason: result.reason,
                relevance: result.relevance,
                examples: result.examples,
                rolePrompt: result.rolePrompt,
                company_count: result.company_count,
                bid: result.bid,
                distribution: result.distribution,
                pricePerBooking: result.bid || 100 // Use AI-generated bid as default
            }));
            setPersonas(newPersonas);

            // Once we have personas, move to pricing step
            if (newPersonas.length > 0 && step === AnalysisStep.GENERATING) {
                setStep(AnalysisStep.PRICING);
            }
        }
    }, [streamedPersonas, step]);

    const handleSubmit = async () => {
        if (!inputValue.trim()) return;

        console.log('Starting analysis for:', inputValue);
        setStep(AnalysisStep.ANALYZING);
        setPersonas([]);
        setAnalyzedBusiness('');

        // Determine if input is a URL or description
        const isUrl = inputValue.includes('.') && (inputValue.startsWith('http') || !inputValue.includes(' '));

        if (isUrl) {
            // Analyze website first
            const url = inputValue.includes('http') ? inputValue : `https://${inputValue}`;
            console.log('Analyzing website:', url);
            await analyzeWebsite('', {
                body: {
                    productInfo: '',
                    url: url
                }
            });
        } else {
            // Skip website analysis, go straight to persona generation
            console.log('Analyzing business description:', inputValue);
            setAnalyzedBusiness(inputValue);
            setStep(AnalysisStep.GENERATING);
            generatePersonas({
                prompts: [],
                query: inputValue
            });
        }
    };

    const handlePriceUpdate = (index: number, price: number) => {
        setPersonas(personas.map((p, i) => i === index ? { ...p, pricePerBooking: price } : p));
    };

    const getTotalEstimatedCost = () => {
        return personas.reduce((sum, p) => sum + (p.pricePerBooking || 50), 0);
    };

    const handleSendToAgentsNetwork = async () => {
        try {
            setStep(AnalysisStep.SENDING);
            if (!sessionRef.current?.siteId) {
                // This should not happen in normal flow, but handle it gracefully
                console.error('No site ID found in session');
                throw new Error('No site ID found in session');
            }

            const response = await fetch(`/api/sites/${sessionRef.current.siteId}/persona-bids`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    businessAnalysis: analyzedBusiness,
                    inputValue: inputValue,
                    personas: personas
                }),
            });

            console.log('Response status:', response.status);
            console.log('Response ok:', response.ok);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API Error:', errorText);
                throw new Error(`Failed to create persona bids: ${response.status} - ${errorText}`);
            }

            // Redirect to dashboard
            window.location.href = `/signup`;

        } catch (error) {
            console.error('Error sending to agents network:', error);
            setStep(AnalysisStep.READY);
        }
    };

    const scrollToTopAndReset = () => {
        // Scroll to top of page
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Focus on the input field after a short delay to allow scrolling
        setTimeout(() => {
            const input = document.querySelector('input[type="text"]') as HTMLInputElement;
            if (input) {
                input.focus();
                if (!input.value || input.value === '') {
                    input.value = 'https://';
                }
            }
        }, 500);
    };

    return (
        <section className="relative px-6 pt-20 pb-16 lg:px-8">
            <div className="mx-auto max-w-6xl text-center">
                <Badge className="mb-6 bg-teal-100 text-teal-700 hover:bg-teal-200">
                    Powered by AI Agents from 100+ Elite GTM Agencies
                </Badge>

                <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl md:text-6xl lg:text-7xl">
                    Your Calendar Filled,
                    <br />
                    <span className="bg-gradient-to-r from-teal-500 to-green-500 bg-clip-text text-transparent">On Autopilot.</span>
                </h1>

                <p className="mt-6 text-lg sm:text-xl leading-7 sm:leading-8 text-gray-600 max-w-3xl mx-auto px-4 sm:px-0">
                    Define what a booking is worth. Our AI Agents get them. You only pay for
                    confirmed meetings. Maximum ROI, minimum effort.
                </p>

                <motion.div
                    className="mt-16 mx-auto"
                    initial={{ maxWidth: '48rem' }}
                    animate={{
                        maxWidth: step === AnalysisStep.PRICING ? '80rem' : '48rem'
                    }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                >
                    <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-8">
                        <div className="space-y-6">
                            <div className="relative group">
                                <div className="absolute inset-0 bg-gradient-to-r from-teal-500/20 to-green-500/20 rounded-2xl blur-sm group-focus-within:blur-md transition-all duration-300"></div>
                                <div className="relative flex gap-3">
                                    <Input
                                        ref={inputRef}
                                        type="text"
                                        placeholder="https://yourwebsite.com or describe your business..."
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                                        className="flex-1 h-16 text-lg pl-6 pr-6 rounded-2xl border-2 border-gray-200 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/20 transition-all duration-300 bg-white shadow-inner"
                                        disabled={step === AnalysisStep.ANALYZING || step === AnalysisStep.GENERATING}
                                        onFocus={(e) => {
                                            if (!e.target.value || e.target.value === '') {
                                                e.target.value = 'https://';
                                                setInputValue('https://');
                                            }
                                        }}
                                    />
                                    <Button
                                        onClick={handleSubmit}
                                        disabled={!inputValue.trim() || step === AnalysisStep.ANALYZING || step === AnalysisStep.GENERATING}
                                        className="h-16 w-16 bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 rounded-2xl flex-shrink-0"
                                    >
                                        {(step === AnalysisStep.ANALYZING || step === AnalysisStep.GENERATING) ? (
                                            <motion.div
                                                animate={{ rotate: 360 }}
                                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                            >
                                                <Search className="h-6 w-6 text-white" />
                                            </motion.div>
                                        ) : (
                                            <ArrowRight className="h-6 w-6 text-white" />
                                        )}
                                    </Button>
                                </div>
                            </div>

                            <div className="text-center">
                                <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                    {step === AnalysisStep.IDLE && "See how much a booking costs with AI agents"}
                                    {step === AnalysisStep.ANALYZING && "Analyzing your business..."}
                                    {step === AnalysisStep.GENERATING && "Finding customer personas..."}
                                    {step === AnalysisStep.PRICING && "Set your booking prices and send to our agent network"}
                                    {step === AnalysisStep.SENDING && "Processing your agent network request..."}
                                    {step === AnalysisStep.READY && "Ready to send to agents!"}
                                    {step === AnalysisStep.SENDING && "Sending to agents network..."}
                                </p>
                            </div>

                            {/* Business Summary - Shows during analysis and persists */}
                            <AnimatePresence>
                                {(step === AnalysisStep.ANALYZING || step === AnalysisStep.GENERATING || (step === AnalysisStep.PRICING && analyzedBusiness)) && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="bg-teal-50 rounded-2xl p-6 border border-teal-200"
                                    >
                                        <h4 className="font-semibold text-teal-900 mb-3 flex items-center gap-2">
                                            {step === AnalysisStep.ANALYZING && !websiteAnalysis && (
                                                <>
                                                    <motion.div
                                                        animate={{ rotate: 360 }}
                                                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                                    >
                                                        <Search className="w-5 h-5 text-teal-500" />
                                                    </motion.div>
                                                    Crawling...
                                                </>
                                            )}
                                            {step === AnalysisStep.ANALYZING && websiteAnalysis && !analyzedBusiness && (
                                                <>
                                                    <motion.div
                                                        animate={{ scale: [1, 1.2, 1] }}
                                                        transition={{ duration: 1.5, repeat: Infinity }}
                                                    >
                                                        <Sparkles className="w-5 h-5 text-teal-500" />
                                                    </motion.div>
                                                    Writing...
                                                </>
                                            )}
                                            {(analyzedBusiness || step === AnalysisStep.GENERATING || step === AnalysisStep.PRICING) && (
                                                "Business Summary"
                                            )}
                                        </h4>

                                        {/* Show streaming content or final analysis */}
                                        {websiteAnalysis || analyzedBusiness ? (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="text-sm text-teal-800 leading-relaxed"
                                            >
                                                {analyzedBusiness || websiteAnalysis}
                                            </motion.div>
                                        ) : (
                                            <div className="text-sm text-teal-700">
                                                Analyzing your website and business model...
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Persona Generation Status */}
                            <AnimatePresence>
                                {step === AnalysisStep.GENERATING && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="bg-gray-50 rounded-2xl p-6 border border-gray-200"
                                    >
                                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                            <motion.div
                                                animate={{ rotate: 360 }}
                                                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                                            >
                                                <Sparkles className="w-5 h-5 text-green-500" />
                                            </motion.div>
                                            Finding Customer Personas
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            Identifying your ideal customer segments for AI agent targeting...
                                        </p>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Pricing Step - Inside Search Area */}
                            <AnimatePresence>
                                {(step === AnalysisStep.PRICING || step === AnalysisStep.SENDING) && personas.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="space-y-6"
                                    >
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-4 text-center">
                                                Set Your Booking Value for Each Customer Segment
                                            </h4>
                                            <p className="text-sm text-gray-600 text-center mb-6">
                                                How much is a qualified meeting worth to you for each persona? You only pay when we deliver.
                                            </p>
                                        </div>

                                        {/* Personas Grid - Compact for inside search area */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {personas.map((persona, index) => (
                                                <motion.div
                                                    key={index}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: index * 0.1 }}
                                                >
                                                    <Card className="border-0 shadow-md hover:shadow-lg transition-shadow bg-white">
                                                        <CardContent className="p-4">
                                                            <div className="mb-3">
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <Badge variant="secondary" className="text-xs">
                                                                        {Math.round(persona.relevance * 100)}% Match
                                                                    </Badge>
                                                                </div>
                                                                <h5 className="font-semibold text-gray-900 text-sm line-clamp-2">
                                                                    {persona.rolePrompt} at {persona.prompt}
                                                                </h5>
                                                            </div>

                                                            <p className="text-xs text-gray-600 mb-3 line-clamp-2">{persona.reason}</p>

                                                            {/* Price Slider with Distribution */}
                                                            <div className="border-t pt-3">
                                                                <div className="space-y-3">
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-xs font-medium text-gray-700">Price per booking:</span>
                                                                        <span className="text-sm font-bold text-green-600">
                                                                            ${persona.pricePerBooking || persona.bid || 100}
                                                                        </span>
                                                                    </div>

                                                                    {/* Distribution visualization */}
                                                                    {persona.distribution ? (
                                                                        <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
                                                                            {/* Distribution curve background */}
                                                                            <div className="absolute inset-0 flex items-end justify-center">
                                                                                <div className="w-full h-full relative">
                                                                                    {/* Generate distribution bars using AI distribution data */}
                                                                                    {Array.from({ length: 20 }, (_, i) => {
                                                                                        const position = (i / 19) * 100; // 0-100%
                                                                                        const priceAtPosition = persona.distribution!.min + (position / 100) * (persona.distribution!.max - persona.distribution!.min);
                                                                                        const distanceFromMean = Math.abs(priceAtPosition - persona.distribution!.mean);
                                                                                        const normalizedDistance = distanceFromMean / persona.distribution!.std;
                                                                                        const height = Math.max(0.1, Math.exp(-Math.pow(normalizedDistance / 2, 2))) * 100;

                                                                                        return (
                                                                                            <div
                                                                                                key={i}
                                                                                                className="absolute bottom-0 bg-blue-200 opacity-60"
                                                                                                style={{
                                                                                                    left: `${position}%`,
                                                                                                    width: '5%',
                                                                                                    height: `${height}%`,
                                                                                                }}
                                                                                            />
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>

                                                                            {/* Current value indicator */}
                                                                            <div
                                                                                className="absolute top-0 bottom-0 w-1 bg-green-500 shadow-lg"
                                                                                style={{
                                                                                    left: `${Math.max(0, Math.min(100, ((persona.pricePerBooking || persona.bid || 100) - persona.distribution!.min) / (persona.distribution!.max - persona.distribution!.min) * 100))}%`,
                                                                                    transform: 'translateX(-50%)'
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    ) : (
                                                                        <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
                                                                            <div className="absolute inset-0 flex items-center justify-center">
                                                                                <div className="text-xs text-gray-500">Loading distribution...</div>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* Price labels */}
                                                                    <div className="flex justify-between text-xs text-gray-500">
                                                                        <span>${persona.distribution?.min ?? Math.max(10, (persona.bid || 100) - 50)}</span>
                                                                        <span>${persona.distribution?.mean ?? (persona.bid || 100)}</span>
                                                                        <span>${persona.distribution?.max ?? (persona.bid || 100) + 50}</span>
                                                                    </div>

                                                                    {/* Slider */}
                                                                    <Slider
                                                                        value={[persona.pricePerBooking || persona.bid || 100]}
                                                                        onValueChange={(value: number[]) => handlePriceUpdate(index, value[0])}
                                                                        min={persona.distribution?.min ?? Math.max(10, (persona.bid || 100) - 50)}
                                                                        max={persona.distribution?.max ?? (persona.bid || 100) + 50}
                                                                        step={5}
                                                                        className="w-full"
                                                                    />

                                                                    {/* Market context */}
                                                                    <div className="text-xs text-center">
                                                                        {persona.distribution ? (
                                                                            <>
                                                                                {(persona.pricePerBooking || persona.bid || 100) < persona.distribution.mean * 0.8 && (
                                                                                    <span className="text-orange-600">Below market average</span>
                                                                                )}
                                                                                {(persona.pricePerBooking || persona.bid || 100) >= persona.distribution.mean * 0.8 && (persona.pricePerBooking || persona.bid || 100) <= persona.distribution.mean * 1.2 && (
                                                                                    <span className="text-green-600">Market average</span>
                                                                                )}
                                                                                {(persona.pricePerBooking || persona.bid || 100) > persona.distribution.mean * 1.2 && (
                                                                                    <span className="text-blue-600">Above market average</span>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <span className="text-gray-500">Calculating market position...</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </CardContent>
                                                    </Card>
                                                </motion.div>
                                            ))}
                                        </div>

                                        {/* Summary and CTA */}
                                        <div className="text-center space-y-4 pt-4 border-t">
                                            <div className="bg-gradient-to-r from-teal-50 to-green-50 rounded-xl p-4">
                                                <p className="text-sm text-gray-700 mb-2">
                                                    <span className="font-semibold">{personas.length} personas</span> •
                                                    <span className="font-semibold text-green-600"> ${Math.round(getTotalEstimatedCost() / personas.length)} avg/booking</span>
                                                </p>
                                            </div>

                                            <Button
                                                size="lg"
                                                className="bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600 text-white px-8 py-4 w-full sm:w-auto"
                                                onClick={handleSendToAgentsNetwork}
                                                disabled={step === AnalysisStep.SENDING}
                                            >
                                                {step === AnalysisStep.SENDING ? (
                                                    <>
                                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                        Sending to Network...
                                                    </>
                                                ) : (
                                                    <>
                                                        Send to Agents Network
                                                        <ArrowRight className="ml-2 h-5 w-5" />
                                                    </>
                                                )}
                                            </Button>

                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={scrollToTopAndReset}
                                                className="text-xs"
                                            >
                                                Try Another Business
                                            </Button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

// Static version that shows while session is loading
function StaticNosAgentsHero() {
    return (
        <section className="relative px-6 pt-20 pb-16 lg:px-8">
            <div className="mx-auto max-w-6xl text-center">
                <Badge className="mb-6 bg-teal-100 text-teal-700 hover:bg-teal-200">
                    Powered by AI Agents from 100+ Elite GTM Agencies
                </Badge>

                <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl md:text-6xl lg:text-7xl">
                    Your Calendar Filled,
                    <br />
                    <span className="bg-gradient-to-r from-teal-500 to-green-500 bg-clip-text text-transparent">On Autopilot.</span>
                </h1>

                <p className="mt-6 text-lg sm:text-xl leading-7 sm:leading-8 text-gray-600 max-w-3xl mx-auto px-4 sm:px-0">
                    Define what a booking is worth. Our AI Agents get them. You only pay for
                    confirmed meetings. Maximum ROI, minimum effort.
                </p>

                <div className="mt-16 mx-auto max-w-2xl">
                    <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-8">
                        <div className="space-y-6">
                            <div className="relative group">
                                <div className="absolute inset-0 bg-gradient-to-r from-teal-500/20 to-green-500/20 rounded-2xl blur-sm transition-all duration-300"></div>
                                <div className="relative flex gap-3">
                                    <Input
                                        type="text"
                                        placeholder="https://yourwebsite.com or describe your business..."
                                        value="https://"
                                        disabled
                                        className="flex-1 h-16 text-lg pl-6 pr-6 rounded-2xl border-2 border-gray-200 transition-all duration-300 bg-gray-50 shadow-inner"
                                    />
                                    <Button
                                        disabled
                                        className="h-16 w-16 bg-gradient-to-r from-teal-500 to-green-500 shadow-lg rounded-2xl flex-shrink-0 opacity-50"
                                    >
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                    </Button>
                                </div>
                            </div>

                            <div className="text-center">
                                <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                    Initializing AI agents...
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// Main component following the hero-client.tsx pattern
function NosAgentsHeroWrapper({ initialSession }: { initialSession: Session | null }) {
    const { data: session, status } = useSession();
    const sessionRef = useRef<Session | null>(initialSession);

    useEffect(() => {
        console.log('NosAgentsHero: Session effect triggered', {
            session,
            status,
            currentSessionRef: sessionRef.current,
            sessionSiteId: session?.siteId,
            sessionRefSiteId: sessionRef.current?.siteId
        });

        if (!session) {
            console.log('NosAgentsHero: Waiting for session', { session, status });
            return;
        }
        if (session && sessionRef.current?.siteId !== session?.siteId) {
            console.log('NosAgentsHero: Session updated', {
                oldSession: sessionRef.current,
                newSession: session
            });
            sessionRef.current = session;
        }
    }, [session, status]);

    // Show interactive version for both logged-in users and landing page visitors
    // But ensure session is properly loaded for logged-in users
    console.log('NosAgentsHero render decision:', {
        sessionRefExists: !!sessionRef.current,
        sessionRefSiteId: sessionRef.current?.siteId,
        sessionStatus: status,
        sessionData: session
    });

    // If session is loading, show static version
    if (status === 'loading' || !sessionRef.current || !sessionRef.current.siteId) {
        return <StaticNosAgentsHero />;
    }

    // Once session is loaded (or determined to be unauthenticated), show interactive
    return <NosAgentsHeroWithSession sessionRef={sessionRef} />;
}

// Main export that wraps with SessionProvider
export function NosAgentsHero({ initialSession }: { initialSession: Session | null }) {
    return (
        <SessionProvider refetchOnWindowFocus={false} refetchInterval={0}>
            <NosAgentsHeroWrapper initialSession={initialSession} />
        </SessionProvider>
    );
}
